import { Component, OnInit } from '@angular/core';
import { RouteAlgoAdminService } from '../../../services/route-algo-admin.service';
import { Subject } from 'rxjs';
import { IRouteRecommendationOrganisationDetailFilter } from '../../../interfaces/route-recommendations/route-recommendation-organisation-detail-filter';
import { IPageResult } from '../../../interfaces/page-result';
import { IRouteRecommendationsOrganisationDetail } from '../../../interfaces/route-recommendations/route-recommendation-organisation-detail';
import { LazyLoadEvent, MessageService, SelectItem, Table } from 'primeng';
import { debounceTime } from 'rxjs/operators';
import { Message } from '../../../classes/Message';

@Component({
  selector: 'app-route-algo-admin-organisation-manager',
  templateUrl: './route-algo-admin-organisation-manager.component.html',
  styleUrls: ['./route-algo-admin-organisation-manager.component.css'],
})
export class RouteAlgoAdminOrganisationManagerComponent implements OnInit {
  public readonly itemsPerPageDef = 20;

  public filterVal: IRouteRecommendationOrganisationDetailFilter;
  public filter: Subject<IRouteRecommendationOrganisationDetailFilter>;

  public isLoaded: boolean = false;
  public isLoading: boolean = false;

  public page: IPageResult<IRouteRecommendationsOrganisationDetail> = {
    items: [],
    total: 0,
  };

  public orgStatusOptions: SelectItem[] = [];
  public enabledOptions: SelectItem[] = [];

  public selectedOrganisation: IRouteRecommendationsOrganisationDetail;

  constructor(
    public routeAlgoAdminService: RouteAlgoAdminService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.initDefaultFilters();
    this.loadSelectOptions();

    this.filter = new Subject<IRouteRecommendationOrganisationDetailFilter>();

    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    this.filter.next(this.filterVal);
  }

  initDefaultFilters() {
    this.filterVal = <IRouteRecommendationOrganisationDetailFilter>{
      first: 0,
      rows: this.itemsPerPageDef,
      sortField: 'SupplierName',
      sortOrder: 1,
      filters: {
        supplierName: {},
        organisationStatus: { value: 'active' },
        plannedRoutesEnabled: { value: 'enabled' },
      },
    };
  }

  resetFilters(dt: Table): void {
    dt.sortField = '';
    dt.rows = this.itemsPerPageDef;
    this.initDefaultFilters();
    this.filter.next(this.filterVal);
  }

  load(filterCommand: IRouteRecommendationOrganisationDetailFilter) {
    this.selectedOrganisation = null;
    this.isLoading = true;
    this.routeAlgoAdminService
      .getOrganisations(filterCommand)
      .then((result) => {
        this.page.items = result.items;
        this.page.total = result.total;
        this.isLoaded = true;
        this.isLoading = false;
      })
      .catch(() => {
        this.isLoading = false;
        this.messageService.add(
          Message.Error(
            'An error occurred when attempting to load the organisations. Please try again.',
          ),
        );
      });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    this.filter.next(this.filterVal);
  }

  search(): void {
    this.filterVal.first = 0;
    this.filter.next(this.filterVal);
  }

  loadSelectOptions(): void {
    this.orgStatusOptions.push({ label: 'Show All', value: 'all' });
    this.orgStatusOptions.push({ label: 'Active', value: 'active' });
    this.orgStatusOptions.push({ label: 'Suspended', value: 'suspended' });
    this.orgStatusOptions.push({ label: 'Inactive', value: 'inactive' });

    this.enabledOptions.push({ label: 'Show All', value: 'all' });
    this.enabledOptions.push({ label: 'Planned Routes Enabled', value: 'enabled' });
    this.enabledOptions.push({
      label: 'Planned Routes Disabled',
      value: 'disabled',
    });
  }

  toggleEnabledStatus(): void {
    if (this.selectedOrganisation == null) return;

    const newStatus = !this.selectedOrganisation.plannedRoutesEnabled;

    this.routeAlgoAdminService
      .setRecommendationAccessStatus(this.selectedOrganisation.id, newStatus)
      .then(() => {
        this.messageService.add(
          Message.Success(
            `Juno Access ${newStatus ? 'Enabled' : 'Disabled'} for ${
              this.selectedOrganisation.name
            }`,
          ),
        );

        this.load(this.filterVal);
      })
      .catch((err) => {
        this.messageService.add(Message.Error('Failed to update Juno status.'));
        console.error(err);
      });
  }
}
